<template>
    <v-dialog :value="value" max-width="1440px" scrollable persistent @input="$emit('input', $event)">
        <v-card class="trip-manager">
            <v-card-title class="trip-manager__header">
                <span class="custom-modal-title text-uppercase black--text font-weight-bold">
                    Gestão da OS #{{ tripId }}
                </span>
                <v-chip v-if="trip" small dark class="ml-3" :color="statusColor(trip.status)">
                    {{ statusName(trip.status) }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn icon :disabled="busy" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

            <v-card-text class="trip-manager__body">
                <div v-if="trip" class="trip-manager__grid">
                    <div class="trip-manager__side">

                        <!-- 1. DADOS DA OS -->
                        <section class="trip-manager__section">
                            <div class="trip-manager__section-title">
                                <span>1</span>
                                <div><strong>Dados da OS</strong><small>Tipo, data, motorista, veículo e valores.</small></div>
                            </div>

                            <v-form ref="osForm">
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <v-select dense outlined hide-details="auto" label="Tipo de viagem"
                                            :items="tripTypes" item-value="value" item-text="text"
                                            v-model="form.type" :disabled="!!(trip.passengers && trip.passengers.length)"
                                            :rules="[v => !!v || 'Tipo é obrigatório']"></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select dense outlined hide-details="auto" label="Status"
                                            :items="statusTypes" item-value="id" item-text="name"
                                            v-model="form.status" :rules="[v => !!v || 'Status é obrigatório']"></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field dense outlined hide-details="auto" type="date" label="Data"
                                            v-model="form.date" :rules="[v => !!v || 'Data é obrigatória']"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field dense outlined hide-details="auto" type="time" label="Hora"
                                            v-model="form.time" :rules="[v => !!v || 'Hora é obrigatória']"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-autocomplete dense outlined hide-details="auto" clearable label="Motorista"
                                            :items="driverItems" item-value="id" item-text="name" :filter="() => true"
                                            :loading="lookups.driverLoading" v-model="form.driver_id"
                                            no-data-text="Nenhum motorista encontrado"
                                            @update:search-input="searchDrivers"></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-autocomplete dense outlined hide-details="auto" clearable label="Veículo"
                                            :items="vehicleItems" item-value="id" item-text="label" :filter="() => true"
                                            :loading="lookups.vehicleLoading" v-model="form.vehicle_id"
                                            no-data-text="Nenhum veículo encontrado"
                                            @update:search-input="searchVehicles"></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" :sm="canViewAmounts ? 6 : 12">
                                        <v-select dense outlined hide-details="auto" clearable label="Pedágio"
                                            :items="lookups.tolls" item-value="id" item-text="name"
                                            v-model="form.toll_id"></v-select>
                                    </v-col>
                                    <v-col v-if="canViewAmounts" cols="12" sm="6">
                                        <v-currency-field dense outlined clearable prefix="R$" :default-value="null"
                                            label="Pedágio manual" placeholder="Automático" hide-details="auto"
                                            v-model="form.manual_toll_value"></v-currency-field>
                                    </v-col>
                                    <template v-if="canViewAmounts">
                                        <v-col cols="12" :sm="form.manual_value ? 6 : 12">
                                            <v-switch dense hide-details class="mt-1" color="primary"
                                                label="Definir valor total da viagem" v-model="form.manual_value"></v-switch>
                                        </v-col>
                                        <v-col v-if="form.manual_value" cols="12" sm="6">
                                            <v-currency-field dense outlined prefix="R$" label="Valor total"
                                                hide-details="auto" v-model="form.value"></v-currency-field>
                                        </v-col>
                                    </template>
                                </v-row>

                                <div class="trip-manager__actions">
                                    <span v-if="canViewAmounts && trip.value" class="text-caption grey--text">
                                        Valor atual: R$ {{ formatMoney(trip.value) }}
                                    </span>
                                    <v-spacer></v-spacer>
                                    <v-btn small color="primary" elevation="0" :loading="saving.trip" :disabled="busy"
                                        @click="saveTrip">Salvar dados da OS</v-btn>
                                </div>
                            </v-form>
                        </section>

                        <!-- 2. ROTA -->
                        <section class="trip-manager__section">
                            <div class="trip-manager__section-title">
                                <span>2</span>
                                <div>
                                    <strong>Rota da OS</strong>
                                    <small>Embarques, paradas e desembarques na ordem em que o motorista deve seguir.</small>
                                </div>
                            </div>

                            <v-alert v-if="route && !route.editable" dense text type="info" class="body-2">
                                Viagem finalizada ou cancelada: a rota não pode ser alterada.
                            </v-alert>

                            <div v-if="!routeItems.length" class="text-body-2 grey--text">
                                Adicione passageiros para montar a rota.
                            </div>

                            <draggable v-model="routeItems" handle=".route-item__handle" :disabled="!canEditRoute"
                                @start="dragSnapshot = routeItems.slice()" @end="onReorder">
                                <div v-for="(item, index) in routeItems" :key="item.key">
                                    <div class="route-item" :class="`route-item--${item.kind}`">
                                        <v-icon class="route-item__handle" small :disabled="!canEditRoute"
                                            title="Arraste para mudar a ordem">mdi-drag-vertical</v-icon>

                                        <span class="route-item__badge">{{ index + 1 }}</span>

                                        <div class="route-item__content">
                                            <div class="route-item__title">
                                                <v-icon x-small :color="kindColor(item.kind)" class="mr-1">
                                                    {{ kindIcon(item.kind) }}
                                                </v-icon>
                                                <strong>{{ kindLabel(item.kind) }}</strong>
                                                <template v-if="item.kind !== 'stop'">
                                                    · {{ item.passenger_name || 'Passageiro' }}
                                                </template>
                                                <span v-if="item.time && item.kind !== 'destination'" class="grey--text">
                                                    ({{ formatTime(item.time) }})
                                                </span>
                                                <v-chip v-if="item.done_at" x-small color="success" dark class="ml-2">
                                                    {{ doneLabel(item) }} {{ formatDateTime(item.done_at) }}
                                                </v-chip>
                                            </div>

                                            <template v-if="editingKey === item.key">
                                                <AddressSearchField class="mt-2" autofocus
                                                    :label="`Novo endereço (${kindLabel(item.kind).toLowerCase()})`"
                                                    :value="item.address ? { address: item.address, iid: item.iid } : null"
                                                    @select="value => onAddressSelected(item, value)"
                                                    @error="notifyError" />
                                                <v-row v-if="item.kind === 'stop'" dense class="mt-1">
                                                    <v-col cols="8">
                                                        <v-text-field dense outlined hide-details="auto" maxlength="255"
                                                            label="Observação (opcional)" v-model="item.observation"
                                                            @input="routeDirty = true"></v-text-field>
                                                    </v-col>
                                                    <v-col cols="4">
                                                        <v-text-field dense outlined hide-details="auto" type="time"
                                                            label="Horário" :value="formatTime(item.time)"
                                                            @input="value => { item.time = value || null; routeDirty = true }"></v-text-field>
                                                    </v-col>
                                                </v-row>
                                                <div class="text-right mt-1">
                                                    <v-btn x-small text @click="finishEditing(item)">Concluir</v-btn>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div class="route-item__address" :class="{ 'error--text': !item.address }">
                                                    {{ item.address || 'Endereço não informado' }}
                                                </div>
                                                <div v-if="item.kind === 'stop' && item.observation"
                                                    class="route-item__obs">{{ item.observation }}</div>
                                            </template>
                                        </div>

                                        <div class="route-item__buttons">
                                            <v-btn icon x-small :disabled="!canEditRoute" title="Alterar endereço"
                                                @click="startEditing(item)">
                                                <v-icon small>mdi-pencil-outline</v-icon>
                                            </v-btn>
                                            <v-btn v-if="item.kind === 'stop'" icon x-small color="error"
                                                :disabled="!canEditRoute" title="Remover parada" @click="removeStop(index)">
                                                <v-icon small>mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </div>

                                    <div v-if="canAddStop && index < routeItems.length - 1" class="route-insert">
                                        <v-btn x-small text color="orange darken-2" @click="insertStop(index + 1)">
                                            <v-icon x-small left>mdi-map-marker-plus-outline</v-icon>
                                            Parada aqui
                                        </v-btn>
                                    </div>
                                </div>
                            </draggable>

                            <div class="trip-manager__actions">
                                <v-btn v-if="canAddStop" small outlined color="orange darken-2"
                                    @click="insertStop(routeItems.length)">
                                    <v-icon small left>mdi-map-marker-plus-outline</v-icon>
                                    Adicionar parada
                                </v-btn>
                                <span v-if="stopsConfig.enabled" class="text-caption grey--text ml-2">
                                    {{ stopCount }}/{{ stopsConfig.max }} paradas
                                </span>
                                <span v-else-if="trip.passengers && trip.passengers.length"
                                    class="text-caption grey--text">
                                    Pontos de parada não habilitados para as empresas desta OS
                                </span>
                                <v-spacer></v-spacer>
                                <v-btn v-if="!routeDirty && canEditRoute && routeItems.length > 2" small outlined
                                    color="primary" :loading="saving.optimize" @click="optimizeRoute">
                                    <v-icon small left>mdi-auto-fix</v-icon>
                                    Otimizar rota
                                </v-btn>
                                <template v-if="routeDirty">
                                    <v-btn small text :disabled="busy" @click="loadRoute">Desfazer</v-btn>
                                    <v-btn small color="primary" elevation="0" :loading="saving.route"
                                        :disabled="busy" @click="saveRoute">Salvar rota</v-btn>
                                </template>
                            </div>
                        </section>

                        <!-- 3. PASSAGEIROS -->
                        <section class="trip-manager__section">
                            <div class="trip-manager__section-title">
                                <span>3</span>
                                <div>
                                    <strong>Passageiros ({{ (trip.passengers || []).length }})</strong>
                                    <small>Horário, motivo, centro de custo e valores de cada passageiro.</small>
                                </div>
                            </div>

                            <div v-for="tripPassenger in sortedPassengers" :key="`pax-${tripPassenger.id}`"
                                class="pax-item">
                                <div class="pax-item__content">
                                    <strong>{{ tripPassenger.passenger ? tripPassenger.passenger.name : 'Passageiro' }}</strong>
                                    <span class="grey--text ml-1">{{ formatTime(tripPassenger.time) }}</span>
                                    <div class="text-caption grey--text">
                                        {{ tripPassenger.passenger && tripPassenger.passenger.customer
                                            ? tripPassenger.passenger.customer.name : '' }}
                                        <template v-if="tripPassenger.cost_center"> · CC {{ tripPassenger.cost_center.name ||
                                            tripPassenger.cost_center.code }}</template>
                                        <template v-if="canViewAmounts && tripPassenger.value"> · R$ {{
                                            formatMoney(tripPassenger.value) }}</template>
                                    </div>
                                </div>
                                <v-chip x-small :color="passengerStatusColor(tripPassenger.status)" dark class="mr-2">
                                    {{ passengerStatusName(tripPassenger.status) }}
                                </v-chip>
                                <v-btn icon x-small title="Editar passageiro" :disabled="busy"
                                    @click="requestPassengerForm('edit-passenger', tripPassenger)">
                                    <v-icon small>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn icon x-small color="error" title="Remover passageiro" :disabled="busy"
                                    @click="$emit('remove-passenger', trip, tripPassenger)">
                                    <v-icon small>mdi-account-remove-outline</v-icon>
                                </v-btn>
                            </div>

                            <div class="trip-manager__actions">
                                <v-btn small outlined color="primary" :disabled="busy"
                                    @click="requestPassengerForm('add-passenger')">
                                    <v-icon small left>mdi-account-plus-outline</v-icon>
                                    Adicionar passageiro
                                </v-btn>
                            </div>
                        </section>
                    </div>

                    <!-- MAPA -->
                    <div class="trip-manager__map">
                        <LMap ref="map" class="trip-manager__leaflet mapa-claro" :zoom="11" :center="mapCenter"
                            :options="{ zoomControl: true }">
                            <LTileLayer :url="tileUrl" :attribution="tileAttribution" />
                            <LPolyline v-if="routePath.length > 1" :lat-lngs="routePath" color="#1450b5" :weight="5"
                                :opacity="0.8" />
                            <LMarker v-for="group in markerGroups" :key="group.key" :lat-lng="group.latlng">
                                <LIcon :icon-anchor="[14, 14]" :icon-size="[28, 28]" class-name="trip-manager__icon">
                                    <div class="trip-manager__marker" :style="{ background: kindColor(group.kind) }"
                                        :title="group.title">{{ group.label }}</div>
                                </LIcon>
                            </LMarker>
                        </LMap>

                        <div class="trip-manager__summary">
                            <div>
                                <span class="trip-manager__summary-value">{{ routeDistance }}</span>
                                <span class="trip-manager__summary-label">DISTÂNCIA</span>
                            </div>
                            <div>
                                <span class="trip-manager__summary-value">{{ routeDuration }}</span>
                                <span class="trip-manager__summary-label">DURAÇÃO</span>
                            </div>
                            <div>
                                <span class="trip-manager__summary-value">{{ routeItems.length }}</span>
                                <span class="trip-manager__summary-label">PONTOS</span>
                            </div>
                            <span v-if="routeDirty" class="text-caption orange--text text--darken-2">
                                Salve a rota para recalcular
                            </span>
                            <span v-else-if="route && route.route && route.route.estimated"
                                class="text-caption grey--text">Distância estimada</span>
                        </div>

                        <div class="trip-manager__legend">
                            <span><i :style="{ background: kindColor('origin') }"></i>Embarque</span>
                            <span><i :style="{ background: kindColor('stop') }"></i>Parada</span>
                            <span><i :style="{ background: kindColor('destination') }"></i>Desembarque</span>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- PROPOSTA DE ROTA OTIMIZADA -->
        <v-dialog v-model="optimization.open" max-width="720px" scrollable>
            <v-card v-if="optimization.result">
                <v-card-title class="custom-modal-title text-uppercase black--text font-weight-bold">
                    Rota otimizada
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="saving.route" @click="optimization.open = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-alert v-if="!optimization.result.changed" dense text type="success" class="body-2">
                        A rota atual já é a melhor encontrada.
                    </v-alert>

                    <div class="optimize-compare">
                        <div>
                            <span class="optimize-compare__label">Deslocamento</span>
                            <span>{{ formatMinutes(optimization.result.current.travel_duration) }}</span>
                            <v-icon small>mdi-arrow-right</v-icon>
                            <strong>{{ formatMinutes(optimization.result.optimized.travel_duration) }}</strong>
                            <v-chip v-if="savingPercent > 0" x-small color="success" dark class="ml-1">
                                -{{ savingPercent }}%
                            </v-chip>
                        </div>
                        <div v-if="optimization.result.optimized.distance !== null">
                            <span class="optimize-compare__label">Distância</span>
                            <span>{{ formatKm(optimization.result.current.distance) }}</span>
                            <v-icon small>mdi-arrow-right</v-icon>
                            <strong>{{ formatKm(optimization.result.optimized.distance) }}</strong>
                        </div>
                        <div>
                            <span class="optimize-compare__label">Total com atendimento</span>
                            <strong>{{ formatMinutes(optimization.result.optimized.total_duration) }}</strong>
                            <span class="text-caption grey--text ml-1">({{ serviceTimesText }})</span>
                        </div>
                    </div>

                    <p v-if="optimization.result.source === 'estimate'" class="text-caption orange--text text--darken-2">
                        Roteador indisponível: tempos estimados em linha reta.
                    </p>

                    <div class="optimize-list">
                        <div v-for="item in optimization.result.items" :key="`opt-${item.key}`" class="optimize-list__item">
                            <span class="route-item__badge">{{ item.order }}</span>
                            <v-icon x-small :color="kindColor(item.kind)" class="mx-1">{{ kindIcon(item.kind) }}</v-icon>
                            <div class="optimize-list__text">
                                <strong>{{ kindLabel(item.kind) }}</strong>
                                <template v-if="item.kind !== 'stop'"> · {{ item.passenger_name }}</template>
                                <div class="text-caption grey--text">{{ item.address }}</div>
                            </div>
                            <span v-if="item.eta" class="optimize-list__eta">{{ item.eta }}</span>
                        </div>
                    </div>

                    <template v-if="optimization.result.suggested_times.length">
                        <v-checkbox v-model="optimization.applyTimes" dense hide-details class="mt-4"
                            label="Atualizar os horários dos passageiros (embarque) e das paradas com os sugeridos"></v-checkbox>
                        <v-simple-table v-if="optimization.applyTimes" dense class="mt-2">
                            <thead>
                                <tr>
                                    <th>Ponto</th>
                                    <th>Horário atual</th>
                                    <th>Sugerido</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in optimization.result.suggested_times" :key="`time-${row.key}`">
                                    <td>
                                        <v-icon x-small :color="kindColor(row.kind)" class="mr-1">{{ kindIcon(row.kind) }}</v-icon>
                                        {{ row.label }}
                                    </td>
                                    <td>{{ row.current_time || '-' }}</td>
                                    <td :class="{ 'font-weight-bold primary--text': row.current_time !== row.suggested_time }">
                                        {{ row.suggested_time }}
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                        <p class="text-caption grey--text mt-2 mb-0">
                            Horários calculados a partir do horário da OS, somando o deslocamento e o atendimento.
                        </p>
                    </template>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="saving.route" @click="optimization.open = false">Descartar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="saving.route"
                        :disabled="!optimization.result.changed && !optimization.applyTimes" @click="applyOptimization">
                        Aplicar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import debounce from 'lodash/debounce'
import draggable from 'vuedraggable'
import { LMap, LTileLayer, LMarker, LIcon, LPolyline } from 'vue2-leaflet'

import AddressSearchField from '@/layouts/components/trip/AddressSearchField.vue'
import statusTypes from '@/common/data/service-status-types.data'
import tripPassengerStatus from '@/common/data/trip-passenger-status.data'

const KIND_COLORS = { origin: '#1e63d6', stop: '#f57c00', destination: '#16965a' }
const STATUS_COLORS = { 1: 'grey', 2: 'red', 3: 'green', 4: 'blue', 5: 'purple' }

let newStopId = 0

// Gestão única da OS: dados da OS, rota completa (embarques, paradas e
// desembarques numa sequência só) e passageiros. Adicionar/editar/remover
// passageiro é feito pela tela pai, que já tem esses formulários.
export default {
    name: 'TripManager',
    components: { draggable, LMap, LTileLayer, LMarker, LIcon, LPolyline, AddressSearchField },
    props: {
        value: { type: Boolean, default: false },
        tripId: { type: [Number, String], default: null },
        canViewAmounts: { type: Boolean, default: false }
    },
    data: () => ({
        trip: null,
        route: null,
        routeItems: [],
        routeDirty: false,
        dragSnapshot: null,
        editingKey: null,
        loading: false,
        saving: { trip: false, route: false, point: false, optimize: false },
        optimization: { open: false, result: null, applyTimes: true },
        form: {},
        lookups: { drivers: [], vehicles: [], tolls: [], driverLoading: false, vehicleLoading: false },
        statusTypes,
        tripTypes: [
            { value: 1, text: 'Entrada' },
            { value: 2, text: 'Saída' },
            { value: 3, text: 'Personalizado' }
        ],
        tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        tileAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        mapCenter: [-30.03, -51.23]
    }),
    computed: {
        busy() {
            return this.loading || this.saving.trip || this.saving.route || this.saving.point || this.saving.optimize
        },
        canEditRoute() {
            return !!(this.route && this.route.editable) && !this.busy
        },
        // Paradas: habilitadas se alguma empresa dos passageiros permitir;
        // vale o maior limite entre elas (mesma regra do backend)
        stopsConfig() {
            const configs = ((this.trip && this.trip.passengers) || [])
                .map(item => item.passenger && item.passenger.customer && item.passenger.customer.settings)
                .map(settings => (typeof settings === 'string' ? this.parseJson(settings) : settings) || {})
                .map(settings => settings.stops || {})
                .filter(stops => stops.enabled)

            if (!configs.length) return { enabled: false, max: 0 }

            return { enabled: true, max: Math.max(...configs.map(stops => Number(stops.max) || 5)) }
        },
        stopCount() {
            return this.routeItems.filter(item => item.kind === 'stop').length
        },
        canAddStop() {
            return this.canEditRoute && this.stopsConfig.enabled && this.stopCount < this.stopsConfig.max
        },
        sortedPassengers() {
            return ((this.trip && this.trip.passengers) || []).slice()
                .sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')))
        },
        driverItems() {
            const list = [...this.lookups.drivers]
            const current = this.trip && this.trip.driver
            if (current && !list.some(item => item.id === current.id)) list.unshift(current)
            return list
        },
        vehicleItems() {
            const list = [...this.lookups.vehicles]
            const current = this.trip && this.trip.vehicle
            if (current && !list.some(item => item.id === current.id)) list.unshift(this.vehicleOption(current))
            return list
        },
        routePath() {
            // Com a ordem alterada e não salva, mostra a ligação direta entre os pontos
            if (this.routeDirty || !this.route || !this.route.route) {
                return this.routeItems.filter(this.hasCoords).map(item => [item.latitude, item.longitude])
            }
            return this.route.route.path || []
        },
        markerGroups() {
            const groups = []

            this.routeItems.forEach((item, index) => {
                if (!this.hasCoords(item)) return

                const key = `${Number(item.latitude).toFixed(5)},${Number(item.longitude).toFixed(5)}`
                let group = groups.find(g => g.key === key)

                if (!group) {
                    group = { key, latlng: [item.latitude, item.longitude], orders: [], titles: [], kind: item.kind }
                    groups.push(group)
                }

                group.orders.push(index + 1)
                group.titles.push(`${index + 1}. ${this.kindLabel(item.kind)}${item.passenger_name ? ' - ' + item.passenger_name : ''}`)
            })

            return groups.map(group => ({
                ...group,
                label: group.orders.length > 2
                    ? `${group.orders[0]}-${group.orders[group.orders.length - 1]}`
                    : group.orders.join(','),
                title: group.titles.join('\n')
            }))
        },
        serviceTimesText() {
            const service = (this.optimization.result && this.optimization.result.service_seconds) || {}
            const minutes = seconds => Math.round((seconds || 0) / 60)

            return `embarque ${minutes(service.origin)} min por passageiro, desembarque ${minutes(service.destination)} min por local, parada ${minutes(service.stop)} min`
        },
                savingPercent() {
            const result = this.optimization.result
            if (!result || !result.current.travel_duration) return 0
            const saved = 1 - result.optimized.travel_duration / result.current.travel_duration
            return Math.max(0, Math.round(saved * 100))
        },
        routeDistance() {
            const distance = this.route && this.route.route && this.route.route.distance
            return distance || distance === 0 ? `${(distance / 1000).toFixed(1)} km` : '-'
        },
        routeDuration() {
            const duration = this.route && this.route.route && this.route.route.duration
            if (!duration && duration !== 0) return '-'
            const minutes = Math.round(duration / 60)
            return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}min` : `${minutes} min`
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open && this.tripId) this.reload()
            }
        },
        tripId() {
            if (this.value && this.tripId) this.reload()
        }
    },
    methods: {
        async reload() {
            this.loading = true
            this.editingKey = null

            try {
                const [tripResponse] = await Promise.all([
                    axios.get(`api/v2/trips/${this.tripId}`),
                    this.loadRoute(false),
                    this.lookups.tolls.length ? null : this.loadTolls()
                ])

                this.trip = tripResponse.data.content
                this.fillForm()
                this.searchDrivers('')
                this.searchVehicles('')
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.loading = false
            }
        },

        async loadRoute(showLoading = true) {
            if (showLoading) this.loading = true

            try {
                const response = await axios.get(`api/v2/trips/${this.tripId}/route`)
                this.applyRoute(response.data.content)
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                if (showLoading) this.loading = false
            }
        },

        applyRoute(route) {
            this.route = route
            this.routeItems = (route.items || []).map(item => ({ ...item }))
            this.routeDirty = false
            this.editingKey = null
            this.$nextTick(() => setTimeout(this.fitMap, 250))
        },

        fillForm() {
            const trip = this.trip
            this.form = {
                type: trip.type,
                status: trip.status,
                date: trip.date ? moment.utc(trip.date).format('YYYY-MM-DD') : null,
                time: trip.time ? String(trip.time).slice(0, 5) : null,
                driver_id: trip.driver_id,
                vehicle_id: trip.vehicle_id,
                toll_id: trip.toll_id,
                holiday_id: trip.holiday_id,
                manual_toll_value: trip.manual_toll_value,
                manual_value: !!trip.manual_value,
                value: trip.value
            }
        },

        async saveTrip() {
            if (!this.$refs.osForm.validate()) return

            this.saving.trip = true

            try {
                const payload = {
                    vehicle_id: this.form.vehicle_id,
                    driver_id: this.form.driver_id,
                    type: this.form.type,
                    date: this.form.date,
                    time: this.form.time,
                    status: this.form.status,
                    toll_id: this.form.toll_id,
                    holiday_id: this.form.holiday_id
                }

                if (this.canViewAmounts) {
                    const toll = parseFloat(this.form.manual_toll_value)
                    payload.manual_toll_value = isNaN(toll) ? null : toll
                    payload.manual_value = !!this.form.manual_value
                    payload.value = this.form.manual_value ? this.form.value : undefined
                }

                await axios.patch(`api/v2/trips/${this.tripId}`, payload)

                this.$dialog.notify.success('Dados da OS salvos', { position: 'top-right', timeout: 3000 })
                await this.reload()
                this.$emit('updated')
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.saving.trip = false
            }
        },

        // O formulário de passageiro substitui a gestão na tela; alterações de
        // rota não salvas seriam perdidas ao voltar
        requestPassengerForm(event, tripPassenger = null) {
            if (this.routeDirty) {
                this.$dialog.notify.warning('Salve ou desfaça as alterações da rota antes de editar passageiros.',
                    { position: 'top-right', timeout: 4000 })
                return
            }

            if (tripPassenger) this.$emit(event, this.trip, tripPassenger)
            else this.$emit(event, this.trip)
        },

        // ----- Rota -----

        onReorder() {
            const problem = this.orderProblem(this.routeItems)

            if (problem) {
                this.routeItems = this.dragSnapshot || this.routeItems
                this.$dialog.notify.warning(problem, { position: 'top-right', timeout: 4000 })
                return
            }

            this.routeDirty = true
        },

        // Desembarque não pode vir antes do embarque do mesmo passageiro
        orderProblem(items) {
            const boarded = new Set()

            for (const item of items) {
                if (item.kind === 'origin') boarded.add(item.trip_passenger_id)
                if (item.kind === 'destination' && !boarded.has(item.trip_passenger_id)) {
                    return `O desembarque de ${item.passenger_name || 'passageiro'} precisa vir depois do embarque.`
                }
            }

            return null
        },

        insertStop(index) {
            const key = `new-stop-${++newStopId}`

            this.routeItems.splice(index, 0, {
                key,
                kind: 'stop',
                stop_id: null,
                address: null,
                iid: null,
                latitude: null,
                longitude: null,
                observation: '',
                time: null
            })

            this.routeDirty = true
            this.editingKey = key
        },

        removeStop(index) {
            this.routeItems.splice(index, 1)
            this.routeDirty = true
        },

        startEditing(item) {
            // Troca de endereço de embarque/desembarque é salva na hora e
            // recarrega a rota: não pode haver ordem pendente de salvar
            if (item.kind !== 'stop' && this.routeDirty) {
                this.$dialog.notify.warning('Salve ou desfaça as alterações da rota antes de mudar o endereço do passageiro.',
                    { position: 'top-right', timeout: 4000 })
                return
            }

            this.editingKey = this.editingKey === item.key ? null : item.key
        },

        finishEditing(item) {
            if (item.kind === 'stop' && !item.address) {
                this.$dialog.notify.warning('Selecione o endereço da parada ou remova-a.', { position: 'top-right', timeout: 3000 })
                return
            }

            this.editingKey = null
        },

        async onAddressSelected(item, value) {
            if (item.kind === 'stop') {
                Object.assign(item, {
                    iid: value.address.iid,
                    address: value.address.address,
                    city: value.address.city,
                    state: value.address.state,
                    district: value.address.district,
                    latitude: value.coords[0],
                    longitude: value.coords[1]
                })
                this.routeDirty = true
                return
            }

            const confirmed = await this.$dialog.confirm({
                title: 'Alterar endereço',
                text: `Alterar o ${this.kindLabel(item.kind).toLowerCase()} de <strong>${item.passenger_name || 'passageiro'}</strong> para <strong>${value.address.address}</strong>? A distância e o valor do passageiro serão recalculados.`,
                actions: { false: 'Cancelar', true: 'Alterar' }
            })

            if (!confirmed) return

            this.saving.point = true

            try {
                const response = await axios.patch(`api/v2/trips/${this.tripId}/route/points/${item.point_id}`, value)

                this.applyRoute(response.data.content)
                this.$dialog.notify.success('Endereço alterado', { position: 'top-right', timeout: 3000 })

                const tripResponse = await axios.get(`api/v2/trips/${this.tripId}`)
                this.trip = tripResponse.data.content
                this.$emit('updated')
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.saving.point = false
            }
        },

        async saveRoute() {
            if (this.routeItems.some(item => item.kind === 'stop' && !this.hasCoords(item))) {
                this.$dialog.notify.warning('Selecione o endereço de todas as paradas.', { position: 'top-right', timeout: 3000 })
                return
            }

            const problem = this.orderProblem(this.routeItems)

            if (problem) {
                this.$dialog.notify.warning(problem, { position: 'top-right', timeout: 4000 })
                return
            }

            this.saving.route = true

            try {
                const response = await axios.put(`api/v2/trips/${this.tripId}/route`, { items: this.routePayload(this.routeItems) })

                this.applyRoute(response.data.content)
                this.$dialog.notify.success('Rota salva', { position: 'top-right', timeout: 3000 })
                this.$emit('updated')
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.saving.route = false
            }
        },

        routePayload(items) {
            return items.map(item => item.kind === 'stop'
                ? {
                    kind: 'stop',
                    id: item.stop_id || null,
                    coords: [Number(item.latitude), Number(item.longitude)],
                    address: {
                        iid: item.iid,
                        address: item.address,
                        city: item.city,
                        state: item.state,
                        district: item.district
                    },
                    observation: item.observation || null,
                    time: item.time ? this.formatTime(item.time) : null
                }
                : { kind: item.kind, point_id: item.point_id })
        },

        async optimizeRoute() {
            this.saving.optimize = true

            try {
                const response = await axios.post(`api/v2/trips/${this.tripId}/route/optimize`)

                this.optimization = { open: true, result: response.data.content, applyTimes: true }
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.saving.optimize = false
            }
        },

        async applyOptimization() {
            const result = this.optimization.result

            this.saving.route = true

            try {
                const applyTimes = this.optimization.applyTimes
                // Parada leva o horário no próprio item da rota; passageiro vai em passenger_times
                const items = result.items.map(item => item.kind === 'stop' && applyTimes && item.eta
                    ? { ...item, time: item.eta }
                    : item)

                const payload = { items: this.routePayload(items) }

                if (applyTimes) {
                    payload.passenger_times = result.suggested_times
                        .filter(row => row.kind === 'origin')
                        .map(row => ({ trip_passenger_id: row.trip_passenger_id, time: row.suggested_time }))
                }

                const response = await axios.put(`api/v2/trips/${this.tripId}/route`, payload)

                this.applyRoute(response.data.content)
                this.optimization.open = false
                this.$dialog.notify.success('Rota otimizada aplicada', { position: 'top-right', timeout: 3000 })

                if (this.optimization.applyTimes) {
                    const tripResponse = await axios.get(`api/v2/trips/${this.tripId}`)
                    this.trip = tripResponse.data.content
                }

                this.$emit('updated')
            } catch (error) {
                this.notifyError(this.$codes(error))
            } finally {
                this.saving.route = false
            }
        },

        formatMinutes(seconds) {
            if (!seconds && seconds !== 0) return '-'
            const minutes = Math.round(seconds / 60)
            return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}min` : `${minutes} min`
        },

        formatKm(meters) {
            return meters || meters === 0 ? `${(meters / 1000).toFixed(1)} km` : '-'
        },

        fitMap() {
            const map = this.$refs.map && this.$refs.map.mapObject
            if (!map) return

            map.invalidateSize()

            const points = this.routePath.length ? this.routePath : this.markerGroups.map(group => group.latlng)

            if (points.length > 1) map.fitBounds(points, { padding: [40, 40] })
            else if (points.length === 1) map.setView(points[0], 15)
        },

        // ----- Lookups -----

        searchDrivers: debounce(async function (filter) {
            this.lookups.driverLoading = true

            try {
                const result = await axios.get('/api/v2/drivers', { params: { start: 1, limit: 50, search: { name: filter || '' } } })
                this.lookups.drivers = result.data.content.data || []
            } catch (error) {
                console.error('Erro ao buscar motoristas:', error)
            } finally {
                this.lookups.driverLoading = false
            }
        }, 400),

        searchVehicles: debounce(async function (filter) {
            this.lookups.vehicleLoading = true

            try {
                const result = await axios.get('/api/v2/vehicles', { params: { start: 1, limit: 100, search: { plate: filter || '' } } })
                this.lookups.vehicles = (result.data.content.data || []).map(this.vehicleOption)
            } catch (error) {
                console.error('Erro ao buscar veículos:', error)
            } finally {
                this.lookups.vehicleLoading = false
            }
        }, 400),

        async loadTolls() {
            try {
                const result = await axios.get('api/tolls')
                this.lookups.tolls = result.data || []
            } catch (error) {
                console.error('Erro ao buscar pedágios:', error)
            }
        },

        vehicleOption(vehicle) {
            const model = vehicle.model ? vehicle.model.model : 'Veículo'
            const plate = vehicle.plate ? String(vehicle.plate).toUpperCase() : ''
            return { id: vehicle.id, label: `${model} - ${plate}${vehicle.code ? ` (Frota: ${vehicle.code})` : ''}` }
        },

        // ----- Helpers -----

        close() {
            if (this.routeDirty) {
                this.$dialog.confirm({
                    title: 'Rota não salva',
                    text: 'As alterações da rota não foram salvas. Deseja fechar mesmo assim?',
                    actions: { false: 'Continuar editando', true: 'Fechar' }
                }).then(confirmed => { if (confirmed) this.$emit('input', false) })
                return
            }

            this.$emit('input', false)
        },

        hasCoords(item) {
            return item.latitude !== null && item.latitude !== undefined && item.longitude !== null
                && item.longitude !== undefined && !(Number(item.latitude) === 0 && Number(item.longitude) === 0)
        },

        kindLabel(kind) {
            return { origin: 'Embarque', stop: 'Parada', destination: 'Desembarque' }[kind] || kind
        },

        kindIcon(kind) {
            return { origin: 'mdi-account-arrow-up', stop: 'mdi-map-marker-path', destination: 'mdi-flag-checkered' }[kind]
        },

        kindColor(kind) {
            return KIND_COLORS[kind] || '#607d8b'
        },

        doneLabel(item) {
            return { origin: 'Embarcou', stop: 'Chegada', destination: 'Desembarcou' }[item.kind]
        },

        statusName(status) {
            const found = statusTypes.find(item => item.id === Number(status))
            return found ? found.name : '-'
        },

        statusColor(status) {
            return STATUS_COLORS[status] || 'grey'
        },

        passengerStatusName(status) {
            const found = tripPassengerStatus.find(item => Number(item.id || item.value) === Number(status))
            return found ? (found.name || found.text) : 'Pendente'
        },

        passengerStatusColor(status) {
            return { 1: 'grey', 2: 'success', 3: 'error' }[status] || 'grey'
        },

        formatTime(value) {
            return value ? String(value).slice(0, 5) : ''
        },

        formatDateTime(value) {
            return moment(value).format('DD/MM HH:mm')
        },

        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        parseJson(value) {
            try {
                return JSON.parse(value)
            } catch (error) {
                return null
            }
        },

        notifyError(message) {
            this.$dialog.notify.error(message, { position: 'top-right', timeout: 5000 })
        }
    }
}
</script>

<style lang="scss">
.trip-manager__header {
    border-bottom: 1px solid #e5e9f0;
}

.trip-manager__body {
    padding: 0 !important;
}

.trip-manager__grid {
    display: grid;
    grid-template-columns: minmax(420px, 5fr) 7fr;
    min-height: 72vh;
}

.trip-manager__side {
    padding: 16px 20px;
    overflow-y: auto;
    max-height: 78vh;
    border-right: 1px solid #e5e9f0;
}

.trip-manager__section {
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 16px;
}

.trip-manager__section-title {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 12px;

    > span {
        flex: 0 0 24px;
        height: 24px;
        border-radius: 50%;
        background: #e8eefc;
        color: #1450b5;
        font-size: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    > div {
        display: flex;
        flex-direction: column;
    }

    small {
        color: #5a6b85;
    }
}

.trip-manager__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}

.route-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    border-left: 4px solid transparent;
    background: #fafbfd;

    &--origin { border-left-color: #1e63d6; }
    &--stop { border-left-color: #f57c00; background: #fff8f0; }
    &--destination { border-left-color: #16965a; }
}

.route-item__handle {
    cursor: grab;
    margin-top: 2px;
}

.route-item__badge {
    flex: 0 0 22px;
    height: 22px;
    border-radius: 50%;
    background: #33415c;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.route-item__content {
    flex: 1 1 auto;
    min-width: 0;
}

.route-item__title {
    font-size: 13px;
}

.route-item__address {
    font-size: 12.5px;
    color: #33415c;
    word-break: break-word;
}

.route-item__obs {
    font-size: 12px;
    color: #8a5a00;
}

.route-item__buttons {
    display: flex;
    flex-direction: column;
}

.route-insert {
    display: flex;
    justify-content: center;
    height: 22px;
    opacity: 0;
    transition: opacity .15s;

    &:hover {
        opacity: 1;
    }
}

.optimize-compare {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #f4f7fd;
    margin-bottom: 12px;
    font-size: 14px;
}

.optimize-compare__label {
    display: inline-block;
    min-width: 170px;
    color: #5a6b85;
}

.optimize-list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    padding: 4px 8px;
}

.optimize-list__item {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f0f2f6;
    font-size: 13px;

    &:last-child {
        border-bottom: none;
    }
}

.optimize-list__text {
    flex: 1 1 auto;
    min-width: 0;
}

.optimize-list__eta {
    font-weight: 700;
    color: #1450b5;
    margin-left: 8px;
}

.pax-item {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f0f2f6;
}

.pax-item__content {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
}

.trip-manager__map {
    position: relative;
    min-height: 72vh;
}

.trip-manager__leaflet {
    position: absolute;
    inset: 0;
    z-index: 0;
}

.trip-manager__leaflet.mapa-claro .leaflet-tile-pane {
    filter: grayscale(100%) brightness(104%) contrast(88%);
}

.trip-manager__marker {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .35);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.trip-manager__summary {
    position: absolute;
    left: 16px;
    bottom: 16px;
    z-index: 500;
    display: flex;
    align-items: center;
    gap: 20px;
    background: #fff;
    border-radius: 8px;
    padding: 10px 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);

    > div {
        display: flex;
        flex-direction: column;
    }
}

.trip-manager__summary-value {
    font-size: 16px;
    font-weight: 700;
    color: #1450b5;
}

.trip-manager__summary-label {
    font-size: 10px;
    letter-spacing: .06em;
    color: #5a6b85;
}

.trip-manager__legend {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 500;
    display: flex;
    gap: 12px;
    background: #fff;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .12);

    i {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 4px;
    }
}

@media (max-width: 1100px) {
    .trip-manager__grid {
        grid-template-columns: 1fr;
    }

    .trip-manager__side {
        max-height: none;
        border-right: none;
    }

    .trip-manager__map {
        min-height: 420px;
    }
}
</style>
